import apiClient from './axios';

export interface AvailableBalance {
  currency: string;
  balance: number;
  used: number;
  available: number;  
  id: number;
  available_credit_to_request: number;
  credit_balance: number;
  pending_repayments: number;
  fee_repayment: number;
  pending_fee_repayments: number;
  pending_withdraws: number;
  network: string;
}

export interface CreditSummaryResponse {
  credit_fees_owed: number
  available_balances: AvailableBalance;
  // available_balances: Array<AvailableBalance>;
  next_payment_due: string;
  credit_transactions: {
    used: number;
    limit: number;
  };
  credit_fee_payment_period: string;
  days_overdue: number;
  label: string;
  maximum_commitment: number;
  maturity_date: string;
  network: string;
  recycled_capital: number;
  borrower_prepaid_fee_balance: number;
  status: string;
  // Pending transaction fields
  pending_repayments: number;
  pending_fee_repayments: number;
  pending_withdraws: number;
}

// backend now returns an array all credit summary
// this interface is an array of CreditSummaryResponse
export interface AllCreditSummaryResponse extends Array<CreditSummaryResponse> {}

export interface TransactionProps {
  channel: string;
  denom: string;
  amount: number;
  status: string;
  transaction_hash: string;
  transaction_link: string;
  timestamp: string;
  id: number;
  credit_line: number;
  wallet_address?: string;
}

// Draw credit
export interface RequestCreditWalletPayload {
  amount: number;
  wallet_address: string;
  selectedCreditLine: number;
  referenceIds?: string;
}

export interface RequestCreditOfframpPayload {
  amount: number;
  wallet_address: string;
  account_number?: string;
  account_name?: string;
  account_routing_number?: string;
  routing_number?: string;
  provider_bank_name?: string;
  selectedCreditLine: number;
}

export interface RepayOnrampPayload {
  amount: number;
  vendor_number: string;
  vendor_name: string;
  vendor_bank: string;
  account_number: string;
  bank_name: string;
  network: string;
  wallet_address: string;
  routing_number: string;
}

export interface RequestCreditWalletResponse {
  status: string;
  message: string;
  request_id: string;
  amount: number;
  wallet_address: string;
  timestamp: string;
}

export interface RepayCreditResponse {
  repayment_address: string;
}

export interface UsePrepaidFeePayload {
  amount: number;
}

export interface UsePrepaidFeeResponse {
  message: string;
  remaining_balance: number;
}

export const getAllCreditSummaries = async (): Promise<AllCreditSummaryResponse> => {
  const response = await apiClient.get<AllCreditSummaryResponse>('/company/credit_summary/');

  // Check if response data exists
  if (!response.data) {
    throw new Error('No data received from credit summary API');
  }
  
  // Check if response data is an array
  if (!Array.isArray(response.data)) {
    throw new Error('Invalid credit summaries data structure - expected array');
  }
  
  return response.data;
};

export const getCreditSummary = async (id: number): Promise<CreditSummaryResponse> => {
  const response = await apiClient.get<AllCreditSummaryResponse>('/company/credit_summary/');

  // Check if response data exists
  if (!response.data) {
    throw new Error('No data received from credit summary API');
  }
  
  // Check if response data is an array
  if (!Array.isArray(response.data)) {
    throw new Error('Invalid credit summaries data structure - expected array');
  }
  
  // Find the credit line by its actual ID, not array index
  const creditLine = response.data.find(line => line.available_balances.id === id);
  
  if (!creditLine) {
    const availableIds = response.data.map(line => line.available_balances.id);
    throw new Error(`Credit line with id ${id} not found. Available credit line IDs: ${availableIds.join(', ')}`);
  }
  
  return creditLine;
};

export const getTransactionHistory = async (
  filter: string,
  ordering: string = '-timestamp',
  pageSize: number = 10,
  creditLineId?: number
): Promise<any> => {
  try {
    // Build query parameters
    const params = new URLSearchParams({
      ordering,
      page_size: pageSize.toString()
    });

    // Add credit line filter if provided
    if (creditLineId !== undefined) {
      params.append('credit_line', creditLineId.toString());
    }

    const response = await apiClient.get(
      `/transaction-history/${filter}/?${params.toString()}`
    );
    
    return response.data;
  } catch (error) {
    console.error(`Error fetching transaction history for filter '${filter}':`, error);
    throw error;
  }
};
export const requestCreditWallet = async (
  payload: RequestCreditWalletPayload
): Promise<RequestCreditWalletResponse> => {
  const creditLineId = payload.selectedCreditLine;
  const response = await apiClient.post<RequestCreditWalletResponse>(
    `/credit_line/${creditLineId}/request-credit-wallet/`,
    payload
  );
  return response.data;
};

export const repayCredit = async (creditLineId: number): Promise<RepayCreditResponse> => {
  const response = await apiClient.get<RepayCreditResponse>(`/credit_line/${creditLineId}/pay-credit-wallet/`);
  return response.data;
};

export const payFees = async (creditLineId: number): Promise<RepayCreditResponse> => {
  const response = await apiClient.get<RepayCreditResponse>(`/credit_line/${creditLineId}/pay-fees-wallet/`);
  return response.data;
};

export const submitPrepaidFee = async (payload: UsePrepaidFeePayload): Promise<UsePrepaidFeeResponse> => {
  const response = await apiClient.post<UsePrepaidFeeResponse>('/company/use-prepaid-fee/', payload);
  return response.data;
};

export const requestCreditOfframp = async (
  payload: RequestCreditOfframpPayload
): Promise<RequestCreditWalletResponse> => {
  const creditLineId = payload.selectedCreditLine;
  const response = await apiClient.post<RequestCreditWalletResponse>(
    `/credit_line/${creditLineId}/request-credit-off-ramp/`,
    payload
  );
  return response.data;
};

export const repayOnramp = async (
  creditLineId: number,
  payload: RepayOnrampPayload
): Promise<any> => {
  const response = await apiClient.post(
    `/credit_line/${creditLineId}/repay-credit-on-ramp/`,
    payload
  );
  return response.data;
};

export const payFeesOnramp = async (
  payload: RepayOnrampPayload
): Promise<any> => {
  const response = await apiClient.post(
    '/credit_line/pay-fees-on-ramp/',
    payload
  );
  return response.data;
};

export const getLinkioVendors = async (
  pageSize: number = 10
): Promise<any> => {
  const response = await apiClient.get(
    `/linkio/vendors/?page_size=${pageSize}`
  );
  return response.data;
};
